import { isNoFurniture } from "@archinventory/client";
import { formatItemNumber, type ItemFields } from "@archinventory/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Trash2, Undo2 } from "lucide-react";
import { ActivityTimeline } from "../../../components/activity-timeline";
import { AiSources } from "../../../components/ai-sources";
import { ItemForm } from "../../../components/item-form";
import { Page } from "../../../components/page";
import { PhotoGallery } from "../../../components/photo-gallery";
import { StatusBadge } from "../../../components/status-badge";
import { Button } from "../../../components/ui/button";
import { Label, Select } from "../../../components/ui/input";
import { api, qk, unwrap } from "../../../lib/api";
import { toastError, useCan, useMe } from "../../../lib/hooks";
import { usePatchItem } from "../../../lib/items";
import { spaceOptions } from "../../../lib/spaces";
import { formatDate } from "../../../lib/utils";

export const Route = createFileRoute("/_app/items/$itemId")({ component: ItemPage });

function ItemPage() {
  const { itemId } = Route.useParams();
  const me = useMe();
  const isAdmin = useCan("admin");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: qk.item(itemId),
    queryFn: () => unwrap(api.items[":id"].$get({ param: { id: itemId } })),
  });
  const item = q.data?.item;
  const projectId = item?.projectId ?? "";
  const project = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => unwrap(api.projects[":id"].$get({ param: { id: projectId } })),
    enabled: !!projectId,
  });
  const members = useQuery({ queryKey: qk.members, queryFn: () => unwrap(api.org.members.$get()) });
  const audit = useQuery({
    queryKey: [...qk.item(itemId), "audit", item?.version],
    queryFn: () => unwrap(api.items[":id"].audit.$get({ param: { id: itemId } })),
    enabled: !!item,
  });
  const patch = usePatchItem(projectId);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.item(itemId) });
  // "Re-analyze with my input": save what's typed first, so it becomes a hint.
  const reanalyze = useMutation({
    mutationFn: async (changed: Partial<ItemFields>) => {
      if (Object.keys(changed).length)
        await patch.mutateAsync({ id: itemId, patch: { fields: changed } });
      return unwrap(api.items[":id"].reanalyze.$post({ param: { id: itemId } }));
    },
    onSuccess: refresh,
    onError: toastError,
  });
  const latestAnalysis = q.data?.analyses.at(-1);
  const remove = useMutation({
    mutationFn: () => unwrap(api.items[":id"].$delete({ param: { id: itemId } })),
    onSuccess: async () => {
      await refresh();
      await qc.invalidateQueries({ queryKey: qk.itemsAll(projectId) });
    },
    onError: toastError,
  });
  const restore = useMutation({
    mutationFn: () => unwrap(api.items[":id"].restore.$post({ param: { id: itemId } })),
    onSuccess: refresh,
    onError: toastError,
  });

  if (q.isError) return <Page title="Item">Not found.</Page>;
  if (!item) return null;
  const name = (id: string | null) =>
    members.data?.members.find((m) => m.userId === id)?.name ?? "—";
  const canDelete = isAdmin || item.createdBy === me.user.id;

  return (
    <Page
      title={
        <span className="flex items-center gap-3">
          <span className="font-mono">{formatItemNumber(item.number)}</span> {item.name ?? ""}
          <StatusBadge status={item.status} aiError={item.aiError} stage={item.analysisStage} />
        </span>
      }
      subtitle={
        project.data && (
          <Link
            to="/projects/$projectId"
            params={{ projectId }}
            search={{ view: "table" }}
            className="hover:underline"
          >
            {project.data.project.clientName} · {project.data.project.name}
          </Link>
        )
      }
      actions={
        item.deletedAt
          ? isAdmin && (
              <Button variant="outline" onClick={() => restore.mutate()}>
                <Undo2 /> Restore
              </Button>
            )
          : canDelete && (
              <Button
                variant="ghost"
                onClick={() =>
                  confirm("Delete this item?") &&
                  remove.mutate(undefined, {
                    onSuccess: () =>
                      navigate({
                        to: "/projects/$projectId",
                        params: { projectId },
                        search: { view: "table" },
                      }),
                  })
                }
              >
                <Trash2 /> Delete
              </Button>
            )
      }
    >
      {item.deletedAt && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          This item was deleted.
        </p>
      )}
      {item.aiError && (
        <p className="rounded-md bg-warning/20 px-3 py-2 text-sm">
          {isNoFurniture(item.aiError)
            ? item.aiError
            : `Couldn't analyze: ${item.aiError}. Retry or fill in manually.`}
        </p>
      )}
      <PhotoGallery
        photos={item.photos}
        pending={Math.max(0, item.expectedPhotoCount - item.photos.length)}
      />
      <ItemForm
        item={item}
        members={members.data?.members}
        saving={patch.isPending}
        onSave={(fields, confirm) => patch.mutate({ id: item.id, patch: { fields, confirm } })}
        onReanalyze={(changed) => reanalyze.mutate(changed)}
        reanalyzing={reanalyze.isPending}
        lastAnalysisAt={latestAnalysis?.createdAt}
        aside={{
          top: (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="item-room">Room</Label>
                <Select
                  id="item-room"
                  value={item.spaceId ?? ""}
                  onChange={(e) =>
                    patch.mutate({ id: item.id, patch: { spaceId: e.target.value || null } })
                  }
                >
                  <option value="">—</option>
                  {spaceOptions(project.data?.spaces ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                {item.confirmedAt
                  ? `Confirmed by ${name(item.confirmedBy)} · ${formatDate(item.confirmedAt)}`
                  : `Captured by ${name(item.createdBy)} · ${formatDate(item.createdAt)}`}
              </p>
            </>
          ),
          bottom: (
            <>
              {(latestAnalysis?.researchStatus === "done" ||
                latestAnalysis?.researchStatus === "failed") && (
                <div className="rounded-xl border border-border p-5">
                  <AiSources analysis={latestAnalysis} />
                </div>
              )}
              <div className="rounded-xl border border-border p-5">
                <ActivityTimeline
                  created={{ at: String(item.createdAt), by: item.createdBy }}
                  history={q.data?.history ?? []}
                  audit={audit.data?.entries ?? []}
                  failed={
                    item.aiError
                      ? { attempt: item.analysisAttempt, at: String(item.updatedAt) }
                      : undefined
                  }
                  name={name}
                />
              </div>
            </>
          ),
        }}
      />
    </Page>
  );
}
